import { Component, OnInit } from '@angular/core';
import { BmsjHipsterSharedLibsModule, BmsjHipsterSharedModule } from '../../../../../shared';
import { NgIf, UpperCasePipe } from '@angular/common';
import { DesignImportRequest, ExperimentDesignService, MapItem } from '../../experiment-design.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertService } from '../../../../../shared/alert/alert.service';
import { StudyEditorContext } from '../../../../study-editor.context';
import { ImportDesignContext } from '../import-design-context';
import { StudyEditorService } from '../../../../study-editor.service';
import { StudyEntryService } from '../../../../../shared/study/service/study-entry.service';
import { StudyEntryModel } from '../../../../../shared/study/model/study-entry.model';

@Component({
    selector: 'jhi-review-design-modal',
    templateUrl: './review-design-modal.component.html',
    styleUrl: './review-design-modal.component.scss'
})
export class ReviewDesignModalComponent implements OnInit {
    // Make Math available in template
    Math = Math;

    public readonly GID = 8240;
    public readonly DESIGNATION = 8250;
    public readonly ENTRY_NO = 8230;

    constructor(private activeModal: NgbActiveModal,
                private alertService: AlertService,
                private importDesignContext: ImportDesignContext,
                private studyEditorContext: StudyEditorContext,
                private studyEditorService: StudyEditorService,
                private experimentDesignService: ExperimentDesignService,
                private studyEntryService: StudyEntryService) {
    }

    isSaving = false;
    mappedHeader: MapItem[] = [];
    importedData: any[];
    maintainHeaders: boolean;
    entriesMap: Map<string, StudyEntryModel> = new Map();

    // Pagination properties
    currentPage = 1;
    pageSize = 50;
    pageSizeOptions = [50, 75, 100];
    totalItems = 0;
    pagedData: any[] = [];

    ngOnInit(): void {
        this.studyEntryService.getStudyEntries(this.studyEditorContext.studyDetails.id, { }).toPromise().then(
            (paginatedResponse) => {
                this.entriesMap = new Map(
                    paginatedResponse.data.map(entry => [entry.entryNumber.toString(), entry])
                );
                this.updatePagedData();
            });
    }

    finish() {
        this.proceedToSave();
    }

    cancel() {
        this.activeModal.dismiss();
    }

    updatePagedData(): void {
        if (this.importedData && this.importedData.length > 1) {
            // Calculate total items (excluding header row)
            this.totalItems = this.importedData.length - 1;

            // Calculate starting and ending indices for the current page
            const startIndex = (this.currentPage - 1) * this.pageSize + 1; // +1 to skip header row
            const endIndex = Math.min(startIndex + this.pageSize, this.importedData.length);

            // Slice the data to get the current page data
            this.pagedData = this.importedData.slice(startIndex, endIndex);
        } else {
            this.pagedData = [];
            this.totalItems = 0;
        }
    }

    onPageChange(page: number): void {
        this.currentPage = page;
        this.updatePagedData();
    }

    onPageSizeChange(event: any): void {
        this.pageSize = parseInt(event.target.value, 10);
        this.currentPage = 1; // Reset to first page when changing page size
        this.updatePagedData();
    }

    async proceedToSave() {
        this.isSaving = true;

        const designImportRequest: DesignImportRequest = {
            mappedHeaders: this.mappedHeader,
            rowData: this.importedData,
            designSource: this.importDesignContext.designSource,
            maintainHeaderNames: this.maintainHeaders,
        };

        this.experimentDesignService.importDesign(this.studyEditorContext.studyId, designImportRequest).toPromise().then(response => {
            this.isSaving = false;
            this.alertService.success('study-editor.import-design.import-file.mapping.import-success');
            this.activeModal.dismiss();
            this.studyEditorService.triggerRefreshAndWait().then(() => {
                this.studyEditorService.navigateToTab(this.studyEditorContext.studyId, 'study-settings');
            });
        }).finally(() => {
            this.isSaving = false;
            this.activeModal.dismiss();
        });
    }

    getCellDisplayValue(row: any[], header: any): any {
        const entryNoHeaderIndex = this.importedData[0].indexOf('ENTRY_NO');
        const headerIndex = this.importedData[0].indexOf(header.name);

        // For GermplasmDescriptor variables ENTRY_NO, GID and DESIGNATION, use the values from corresponding Study Entries in the study.
        switch (header.variable.id) {
        case this.ENTRY_NO.toString():
            return this.getStudyEntryModel(row[entryNoHeaderIndex])?.entryNumber || '-';

        case this.DESIGNATION.toString():
            return this.getStudyEntryModel(row[entryNoHeaderIndex])?.designation || '-';

        case this.GID.toString():
            return this.getStudyEntryModel(row[entryNoHeaderIndex])?.gid?.toString() || '-';

        default:
            return row[headerIndex] || '-';
        }
    }


    getStudyEntryModel(entryNumber: string): StudyEntryModel | null | undefined {
        return this.entriesMap.get(entryNumber);
    }
}
